import React from 'react';
import styled from 'styled-components';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import Hero from '../components/Hero';
import Section from '../components/Section';
import Button from '../components/Button';
import StepProcess from '../components/StepProcess';
import TemplateShowcase from '../components/TemplateShowcase';
import PricingTable from '../components/PricingTable';
import Testimonials from '../components/Testimonials';
import FAQ from '../components/FAQ';
import FeatureBlocks from '../components/FeatureBlocks';
import FounderInsight from '../components/FounderInsight';
import { FaCheck, FaRocket, FaTools, FaSearch, FaMobileAlt, FaLaptopCode, FaClock, FaEuroSign, FaServer, FaBolt, FaUsers, FaHandshake } from 'react-icons/fa';

// Definimos interfaces que coincidan con las del componente PricingTable
interface Feature {
  name: string;
  included: boolean | 'partial';
  highlight?: boolean;
}

interface PricePlan {
  id: string;
  name: string;
  description: string;
  price: number;
  discountPrice?: number;
  period: string;
  isPopular?: boolean;
  features: Feature[];
  ctaText?: string;
  ctaLink?: string;
  additionalText?: string;
}

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
  max-width: 1200px;
  margin: 4rem auto;
  padding: 0 1rem;
`;

const StatCard = styled.div`
  background-color: var(--white);
  border-radius: var(--border-radius);
  padding: 1.5rem;
  border: 1px solid #eee;
  box-shadow: var(--box-shadow-sm);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--box-shadow);
  }
`;

const IconContainer = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg {
    font-size: 1.5rem;
    color: var(--white);
  }
`;

const StatContent = styled.div`
  text-align: left;
`;

const StatNumber = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--gray-dark);
  margin-bottom: 0.25rem;
  line-height: 1.2;
`;

const StatLabel = styled.div`
  font-size: 0.95rem;
  color: var(--gray);
  font-weight: 500;
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(250px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 3rem auto;
  justify-content: center;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, minmax(250px, 1fr));
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: flex-start;
  text-align: left;
  
  svg {
    color: var(--green);
    margin-right: 1rem;
    margin-top: 0.25rem;
    flex-shrink: 0;
  }
  
  div {
    h4 {
      font-size: 1.25rem;
      margin-bottom: 0.5rem;
    }
    
    p {
      color: var(--gray);
      font-size: 1rem;
      line-height: 1.6;
      margin: 0;
    }
  }
`;

// Añadir estilos para la sección "Sobre nosotros"
const AboutSection = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
  
  @media (min-width: 992px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
    align-items: center;
  }
`;

const AboutContent = styled.div``;

const AboutTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  position: relative;
  padding-bottom: 1rem;
  
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 80px;
    height: 4px;
    background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  }
`;

const AboutDescription = styled.p`
  color: var(--gray);
  font-size: 1.125rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const AboutValuesList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
  margin: 2rem 0;
`;

const AboutValue = styled.div`
  display: flex;
  align-items: flex-start;
`;

const AboutValueIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e9f5ff 0%, #dcebff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  flex-shrink: 0;
  
  svg {
    color: var(--blue);
    font-size: 1.25rem;
  }
`;

const AboutValueText = styled.div``;

const AboutValueTitle = styled.h4`
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const AboutValueDescription = styled.p`
  color: var(--gray);
  font-size: 0.95rem;
  line-height: 1.5;
`;

const AboutImage = styled.div`
  position: relative;
  padding-bottom: 75%; /* 4:3 aspect ratio */
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--box-shadow);
  margin-top: 2rem;
  
  @media (min-width: 992px) {
    margin-top: 0;
  }
  
  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const HomePage = () => {
  // Datos para los componentes
  const steps = [
    {
      number: 1,
      title: "Consulta inicial",
      description: "Analizamos tus necesidades y objetivos de negocio. Realizamos un estudio de palabras clave para definir la mejor estrategia para tu página web barata.",
      image: "/fotos generales para el proyecto/paso-1.webp"
    },
    {
      number: 2,
      title: "Diseño y desarrollo",
      description: "Creamos tu página web barata con React para garantizar máxima velocidad y seguridad. Implementamos diseño responsive, contenido persuasivo y llamadas a la acción estratégicas.",
      image: "/fotos generales para el proyecto/paso-2.webp"
    },
    {
      number: 3,
      title: "Optimización y entrega",
      description: "Optimizamos tu página web barata para SEO y para conversión. En aproximadamente una semana, tu web estará lista para generar negocio y aumentar tus ventas.",
      image: "/fotos generales para el proyecto/paso-3.webp"
    },
  ];
  
  const plans: PricePlan[] = [
    {
      id: "basic",
      name: "Web Básica",
      description: "Ideal para profesionales o pequeños negocios que quieren una presencia online básica.",
      price: 200,
      period: "pago único",
      features: [
        { name: "Hasta 5 páginas", included: true },
        { name: "Diseño responsive", included: true },
        { name: "Optimización SEO básica", included: true },
        { name: "Formulario de contacto", included: true },
        { name: "Enlaces a redes sociales", included: true },
        { name: "Integración de WhatsApp", included: true },
        { name: "Aviso legal y políticas", included: true },
        { name: "Estudio de palabras clave", included: false },
        { name: "Imágenes profesionales", included: false },
        { name: "Conexión con Google Analytics", included: false },
        { name: "Ficha de Google My Business", included: false },
        { name: "Sección 'Sobre nosotros'", included: false },
      ],
      ctaText: "Quiero este plan",
      ctaLink: "/reserva?plan=basico",
      additionalText: "Mantenimiento: 20€/mes"
    },
    {
      id: "standard",
      name: "Web Estándar",
      description: "Para empresas que buscan una web más completa con mejores funcionalidades.",
      price: 300,
      period: "pago único",
      isPopular: true,
      features: [
        { name: "Entre 5-10 páginas", included: true },
        { name: "Diseño responsive", included: true },
        { name: "Optimización SEO avanzada", included: true },
        { name: "Formulario de contacto", included: true },
        { name: "Enlaces a redes sociales", included: true },
        { name: "Integración de WhatsApp", included: true },
        { name: "Aviso legal y políticas", included: true },
        { name: "Estudio de palabras clave", included: true },
        { name: "Imágenes profesionales", included: false },
        { name: "Conexión con Google Analytics", included: false },
        { name: "Ficha de Google My Business", included: true },
        { name: "Sección 'Sobre nosotros'", included: true },
      ],
      ctaText: "Quiero este plan",
      ctaLink: "/reserva?plan=estandar",
      additionalText: "Mantenimiento: 20€/mes"
    },
    {
      id: "advanced",
      name: "Web Avanzada",
      description: "Solución completa para empresas que necesitan una web profesional con todas las funcionalidades.",
      price: 400,
      period: "pago único",
      features: [
        { name: "Entre 10-15 páginas", included: true },
        { name: "Diseño responsive", included: true },
        { name: "Optimización SEO avanzada", included: true },
        { name: "Formulario de contacto", included: true },
        { name: "Enlaces a redes sociales", included: true },
        { name: "Integración de WhatsApp", included: true },
        { name: "Aviso legal y políticas", included: true },
        { name: "Estudio de palabras clave", included: true },
        { name: "Imágenes profesionales", included: true },
        { name: "Conexión con Google Analytics", included: true },
        { name: "Ficha de Google My Business", included: true },
        { name: "Sección 'Sobre nosotros'", included: true },
      ],
      ctaText: "Quiero este plan",
      ctaLink: "/reserva?plan=avanzado",
      additionalText: "Mantenimiento: 20€/mes"
    }
  ];
  
  const testimonials = [
    {
      id: "1",
      quote: "Gracias a Web a Bajo Coste, mi clínica dental ha duplicado las solicitudes de cita a través de la web. El diseño es moderno y transmite exactamente la profesionalidad que quería proyectar. La mejor inversión para mi negocio en años.",
      name: "Dra. Ana Martínez",
      position: "Directora Clínica",
      company: "Clínica Dental Sonrisa",
      image: "/fotos generales para el proyecto/testimonio-1.webp"
    },
    {
      id: "2",
      quote: "Como autónomo, no podía permitirme gastar miles de euros en una web. Web a Bajo Coste me ofreció una solución profesional a un precio que sí pude pagar. Ahora tengo más clientes y he mejorado mi imagen de marca considerablemente.",
      name: "Carlos Rodríguez",
      position: "Fotógrafo Freelance",
      company: "CR Fotografía",
      image: "/fotos generales para el proyecto/testimonio-2.webp"
    },
    {
      id: "3",
      quote: "Nuestra inmobiliaria necesitaba urgentemente una renovación web. El equipo entendió perfectamente nuestras necesidades y creó una web que no solo es bonita sino que convierte visitas en clientes. El retorno de inversión ha sido espectacular.",
      name: "Manuel Fernández",
      position: "Gerente",
      company: "Inmobiliaria Nuevo Hogar",
      image: "/fotos generales para el proyecto/testimonio-3.webp"
    }
  ];
  
  const faqs = [
    {
      id: "1",
      question: "¿Cuánto cuesta una página web barata?",
      answer: "Nuestras páginas web baratas comienzan desde 200€ para sitios web básicos. El precio varía según las funcionalidades que necesites: Web Básica (200€), Web Estándar (300€) y Web Avanzada (400€). Todos nuestros precios son pagos únicos, además del mantenimiento mensual de 20€/mes que incluye hosting, dominio y correos corporativos.",
      category: "Precios"
    },
    {
      id: "2",
      question: "¿Cuánto tiempo se tarda en crear una página web barata?",
      answer: "El tiempo de desarrollo de tu página web barata es de aproximadamente una semana, dependiendo de la complejidad del proyecto. Las landing pages específicas para campañas se entregan en un máximo de 3 días. Estos plazos comienzan una vez que recibimos todos los materiales necesarios (textos, imágenes, etc.).",
      category: "Tiempos"
    },
    {
      id: "3",
      question: "¿Qué tecnología utilizáis para crear las páginas web?",
      answer: "Desarrollamos nuestras páginas web baratas utilizando React, una tecnología moderna que proporciona sitios web más rápidos, seguros y optimizados que las webs tradicionales en WordPress. Esto se traduce en mejor experiencia de usuario, mayor velocidad de carga (lo que mejora el SEO) y mayor seguridad frente a ataques.",
      category: "Tecnología"
    },
    {
      id: "4",
      question: "¿En qué os diferenciáis de otros servicios de páginas web baratas?",
      answer: "A diferencia de otros servicios que usan WordPress y limitan el número de 'bloques', nosotros hacemos un keyword research personalizado, desarrollamos en React para mayor velocidad, creamos contenido persuasivo orientado a la conversión, no limitamos la extensión de las páginas, incluimos llamadas a la acción (CTAs) estratégicas, e implementamos todos los aspectos legales necesarios. Además, tu web no es una sola página, sino varias páginas interconectadas de forma óptima para SEO.",
      category: "Servicios"
    },
    {
      id: "5",
      question: "¿Qué incluye el mantenimiento mensual?",
      answer: "El mantenimiento mensual de 20€/mes incluye: hosting, dominio, certificado SSL, correos corporativos, soporte técnico, y actualizaciones de seguridad. Este servicio es obligatorio para garantizar que tu web esté siempre online y funcionando correctamente. Se paga mes a mes por domiciliación bancaria.",
      category: "Mantenimiento"
    }
  ];
  
  const features = [
    {
      title: "Diseño responsive que se adapta a cualquier dispositivo",
      description: "Tu página web barata se verá perfectamente en móviles, tablets y ordenadores, garantizando una experiencia óptima para todos tus visitantes. Esta adaptabilidad es fundamental para el SEO moderno y para captar usuarios de dispositivos móviles.",
      image: "/fotos generales para el proyecto/responsive-design.webp",
      ctaText: "Ver tipos de webs",
      ctaLink: "/servicios"
    },
    {
      title: "React en lugar de WordPress para mayor rendimiento",
      description: "A diferencia de otros servicios de páginas web baratas que usan WordPress, desarrollamos en React. Esto proporciona sitios web más rápidos, seguros y optimizados, lo que mejora tanto la experiencia de usuario como el posicionamiento en buscadores.",
      image: "/fotos generales para el proyecto/react-vs-wordpress.webp",
      ctaText: "Quiero mi Web en React",
      ctaLink: "/reserva"
    },
    {
      title: "Optimización SEO avanzada para generar negocio",
      description: "Todas nuestras páginas web baratas incluyen keyword research y arquitectura SEO personalizada. Implementamos metadatos, estructuras semánticas, URLs amigables y otros factores que ayudan a tu web a posicionarse mejor en Google para tus palabras clave objetivo.",
      image: "/fotos generales para el proyecto/seo-optimization.webp",
      ctaText: "Ver tipos de webs",
      ctaLink: "/servicios"
    }
  ];

  return (
    <Layout>
      <SEO
        title="Web a Bajo Coste: Diseño Web Profesional y Económico ⭐"
        description="Diseño web para pequeños negocios desde 200€. Webs profesionales, rápidas y optimizadas para SEO. ✅ Web optimizada para conseguir clientes."
        keywords="diseño web barato, páginas web para negocios, web profesional económica, diseño web madrid, desarrollo web españa"
        canonical="https://webabajocoste.es"
      />

      <Hero
        title="Diseño Web Profesional desde 200€"
        subtitle="Web optimizada para SEO, con diseño profesional y facilidad de uso. La web perfecta para tu negocio, sin complicaciones."
        ctaText="Quiero mi web"
        ctaLink="/reserva"
        backgroundImage="/fotos generales para el proyecto/hero-image.webp"
        badges={['Desde 200€', 'Pago único', 'Mantenimiento 20€/mes']}
      />
      
      <StatGrid>
        <StatCard>
          <IconContainer>
            <FaClock />
          </IconContainer>
          <StatContent>
            <StatNumber>7 días</StatNumber>
            <StatLabel>Tiempo de entrega</StatLabel>
          </StatContent>
        </StatCard>
        <StatCard>
          <IconContainer>
            <FaEuroSign />
          </IconContainer>
          <StatContent>
            <StatNumber>desde 200€</StatNumber>
            <StatLabel>Pago único</StatLabel>
          </StatContent>
        </StatCard>
        <StatCard>
          <IconContainer>
            <FaServer />
          </IconContainer>
          <StatContent>
            <StatNumber>20€/mes</StatNumber>
            <StatLabel>Mantenimiento fijo</StatLabel>
          </StatContent>
        </StatCard>
        <StatCard>
          <IconContainer>
            <FaBolt />
          </IconContainer>
          <StatContent>
            <StatNumber>React</StatNumber>
            <StatLabel>Más rápida que WordPress</StatLabel>
          </StatContent>
        </StatCard>
      </StatGrid>
      
      <StepProcess 
        title="Cómo creamos tu página web barata sin sacrificar calidad"
        subtitle="A diferencia de otros servicios, nuestro proceso está diseñado para ofrecer más valor por menos dinero"
        steps={steps}
        ctaText="Quiero mi web"
        ctaLink="/reserva"
      />
      
      <TemplateShowcase />
      
      {/* Nueva sección Sobre Nosotros */}
      <Section>
        <AboutSection>
          <AboutContent>
            <AboutTitle>Conoce a nuestros Fundadores</AboutTitle>
            <AboutDescription>
              Somos Jaime y Miguel, fundadores de Web a Bajo Coste. Un equipo de desarrolladores y diseñadores web con más de 10 años de experiencia 
              en el sector digital. Nuestro objetivo es democratizar el acceso a una presencia online 
              de calidad para pequeños negocios y profesionales.
            </AboutDescription>
            <AboutDescription>
              En Web a Bajo Coste hemos optimizado nuestros procesos para ofrecer páginas web profesionales 
              a precios accesibles sin sacrificar calidad ni rendimiento.
            </AboutDescription>
            
            <AboutValuesList>
              <AboutValue>
                <AboutValueIcon>
                  <FaUsers />
                </AboutValueIcon>
                <AboutValueText>
                  <AboutValueTitle>Experiencia</AboutValueTitle>
                  <AboutValueDescription>
                    Más de 10 años en desarrollo web y marketing digital.
                  </AboutValueDescription>
                </AboutValueText>
              </AboutValue>
              
              <AboutValue>
                <AboutValueIcon>
                  <FaHandshake />
                </AboutValueIcon>
                <AboutValueText>
                  <AboutValueTitle>Compromiso</AboutValueTitle>
                  <AboutValueDescription>
                    Entrega en tiempo y forma, sin sorpresas ni costes ocultos.
                  </AboutValueDescription>
                </AboutValueText>
              </AboutValue>
            </AboutValuesList>
            
            <Button 
              as="a" 
              href="/sobre-nosotros" 
              variant="outline"
            >
              Conócenos mejor
            </Button>
          </AboutContent>
          
          <AboutImage>
            <img src="/fotos generales para el proyecto/equipo.webp" alt="Equipo de Web a Bajo Coste" />
          </AboutImage>
        </AboutSection>
      </Section>
      
      {/* Componente de Opinión del Fundador */}
      <FounderInsight 
        insightText="Después de años trabajando con empresas que pagaban miles de euros por webs que no generaban resultados, decidimos crear una solución asequible pero efectiva. Una buena web no tiene por qué ser cara, pero sí debe estar optimizada para convertir visitantes en clientes."
      />
      
      <FeatureBlocks
        title="Ventajas de nuestras páginas web baratas frente a la competencia"
        subtitle="Desarrollamos en React, no en WordPress, y creamos múltiples páginas en lugar de una sola"
        features={features}
      />
      
      <PricingTable
        title="Planes para tu página web barata: Pago único"
        subtitle="Elige el plan que mejor se adapte a tu negocio - Mantenimiento mensual de 20€/mes"
        plans={plans}
        showAnnualToggle={false}
      />
      
      <Testimonials
        testimonials={testimonials}
      />
      
      <Section 
        background="light"
        centered
      >
        <h2 style={{ fontSize: '2.5rem', marginBottom: '1.5rem', textAlign: 'center' }}>¿Por qué elegir nuestra página web barata?</h2>
        <p style={{ fontSize: '1.2rem', color: 'var(--gray)', maxWidth: '800px', margin: '0 auto 3rem', textAlign: 'center' }}>
          Nos diferenciamos de otras agencias ofreciendo valor real a precios accesibles
        </p>
        
        <BenefitsList>
          <BenefitItem>
            <FaCheck /> 
            <div>
              <h4>Keyword research personalizado</h4>
              <p>Investigamos las palabras clave específicas de tu sector para optimizar tu página web barata.</p>
            </div>
          </BenefitItem>
          <BenefitItem>
            <FaCheck /> 
            <div>
              <h4>Tecnología moderna React</h4>
              <p>Tu página web barata se desarrolla con React, no con WordPress como la mayoría, para obtener mejor velocidad de carga.</p>
            </div>
          </BenefitItem>
          <BenefitItem>
            <FaCheck /> 
            <div>
              <h4>Contenido persuasivo</h4>
              <p>Creamos textos orientados a la conversión que transforman visitantes en clientes.</p>
            </div>
          </BenefitItem>
          <BenefitItem>
            <FaCheck /> 
            <div>
              <h4>Sin restricciones de contenido</h4>
              <p>No limitamos la extensión o número de secciones de tu página web barata como hacen otros servicios.</p>
            </div>
          </BenefitItem>
          <BenefitItem>
            <FaCheck /> 
            <div>
              <h4>Múltiples páginas interconectadas</h4>
              <p>A diferencia de otros servicios que ofrecen una sola página, creamos varias páginas conectadas de forma óptima para SEO.</p>
            </div>
          </BenefitItem>
          <BenefitItem>
            <FaCheck /> 
            <div>
              <h4>CTAs estratégicamente ubicadas</h4>
              <p>Implementamos llamadas a la acción que aumentan las conversiones en toda tu página web barata.</p>
            </div>
          </BenefitItem>
        </BenefitsList>
        
        <div style={{ textAlign: 'center', marginTop: '3rem' }}>
          <Button 
            as="a" 
            href="/reserva" 
            size="large"
            style={{ padding: '1rem 2.5rem', textTransform: 'uppercase', fontWeight: '600' }}
          >
            Quiero mi web
          </Button>
        </div>
      </Section>
      
      <FAQ
        items={faqs}
      />
    </Layout>
  );
};

export default HomePage; 